// PublicLanding — the PUBLIC front door for a logged-out visitor (redesign
// A-01), distinct from the app: the fixed brand ink palette, the product mark,
// what it does in one line, one primary CTA, and how it works in three steps.
// Its doors all lead to sign-in.
//
// Mounted by AppShellHost in the anonymous (auth-mode, signed-out) branch,
// BEFORE SignInHost (IDD b3df0043). Never rendered for a signed-in author or
// in fs-mode/local — that gate is AppShellHost's decision.
//
// Pure/props-driven: no local state, no I/O. Every intent reports up. The
// design's illustrative Studio collage is not drawn (it would show example
// content as if real).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { PublicBrand } from "../PublicBrand/PublicBrand.js";
import "./PublicLanding.css";

export type PublicLandingProps = {
    // The primary door — "Get started". Hands off to sign-in.
    readonly onPrimaryCta: () => void;
    // The door for an existing user signed out on this browser.
    readonly onSignIn: () => void;
};

export const PublicLanding = ({
    onPrimaryCta,
    onSignIn,
}: PublicLandingProps): ReactNode => {
    const { t } = useLingui();
    const steps = [
        {
            id: "collect",
            head: t`Collect`,
            text: t`PDFs, links, notes, old courses.`,
        },
        {
            id: "assemble",
            head: t`Put together`,
            text: t`The assistant proposes, you approve and put it together.`,
        },
        { id: "export", head: t`Export`, text: t`To Torus.` },
    ];

    return (
        <div className="pln-page">
            <nav className="pln-nav" aria-label={t`Main navigation`}>
                <PublicBrand />
                <div className="pln-navEnd">
                    <button
                        type="button"
                        className="pln-link"
                        onClick={onSignIn}
                    >
                        {t`Sign in`}
                    </button>
                    <button
                        type="button"
                        className="pln-cta pln-cta--small"
                        onClick={onPrimaryCta}
                    >
                        {t`Get started`}
                    </button>
                </div>
            </nav>

            <main className="pln-hero">
                <h1 className="pln-headline">
                    {t`Turn your sources into teaching material.`}
                </h1>
                <p className="pln-lede">
                    {t`Add your sources, ask the assistant for what you need, and put the parts together into modules and courses. When it is ready, export it to your learning platform.`}
                </p>
                <div className="pln-ctaRow">
                    <button
                        type="button"
                        className="pln-cta"
                        onClick={onPrimaryCta}
                    >
                        {t`Get started`}
                    </button>
                    <button
                        type="button"
                        className="pln-link"
                        onClick={onSignIn}
                    >
                        {t`I already have an account`}
                    </button>
                </div>
                <ol className="pln-steps" aria-label={t`How it works`}>
                    {steps.map((step, index) => (
                        <li key={step.id} className="pln-step">
                            <span className="pln-stepN" aria-hidden="true">
                                {index + 1}
                            </span>
                            <span className="pln-stepHead">{step.head}</span>
                            <span className="pln-stepText">{step.text}</span>
                        </li>
                    ))}
                </ol>
            </main>
        </div>
    );
};
